import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { PartnerCustomerInventoryTag } from "../../../../../models/inventory/PartnerCustomerInventoryTag";
import {
  ProductListApi,
  ProductVariantApi,
} from "../../../../../models/CatalogProducts/types";
import { InventoryInwardLineInput } from "../../../stores/InventoryDetailsStore";

interface InventoryInwardPopupProps {
  open: boolean;
  tags: PartnerCustomerInventoryTag[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSearchSku: (sku: string) => Promise<ProductListApi[]>;
  onFetchVariants: (productId: number) => Promise<ProductVariantApi[]>;
  onSubmit: (lines: InventoryInwardLineInput[]) => Promise<void>;
}

type InwardLineForm = {
  key: string;
  variantId?: number;
  productId: number;
  productName: string;
  variantTitle: string;
  sku: string;
  clientSku: string;
  qty: string;
  packSize: string;
  boxSize: string;
  tagId: string;
  tagLabel: string;
  selected: boolean;
};

const newLineKey = (productId: number, variantId?: number, sku?: string) =>
  `${productId}-${variantId || sku || "product"}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const variantTitle = (variant: ProductVariantApi, fallbackSku: string) =>
  variant.title?.trim() || variant.name?.trim() || fallbackSku || "—";

export default function InventoryInwardPopup({
  open,
  tags,
  isSubmitting = false,
  onClose,
  onSearchSku,
  onFetchVariants,
  onSubmit,
}: InventoryInwardPopupProps) {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [skuSearch, setSkuSearch] = useState("");
  const debouncedSkuSearch = useDebounce(skuSearch, 400);
  const [variantSearch, setVariantSearch] = useState("");
  const debouncedVariantSearch = useDebounce(variantSearch, 300);
  const [isFetchingVariants, setIsFetchingVariants] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [matchedProducts, setMatchedProducts] = useState<ProductListApi[]>([]);
  const [lines, setLines] = useState<InwardLineForm[]>([]);
  const [clientSkuErrors, setClientSkuErrors] = useState<
    Record<string, string>
  >({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const isSubmitLockedRef = useRef(false);
  const linesRef = useRef<InwardLineForm[]>([]);
  linesRef.current = lines;

  const submitInProgress = isSubmitting || isSubmitLocked;

  const tagOptions: FilterValue[] = useMemo(
    () =>
      tags
        .filter((tag) => tag.active !== 0)
        .map((tag) => ({
          id: String(tag.tag_id),
          value: tag.tag,
        })),
    [tags],
  );

  useEffect(() => {
    setIsSubmitLocked(false);
    isSubmitLockedRef.current = false;
    if (!open) {
      setSkuSearch("");
      setVariantSearch("");
      setMatchedProducts([]);
      setLines([]);
      setClientSkuErrors({});
      setSearchError("");
      setSubmitError("");
      setIsFetchingVariants(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const trimmed = debouncedSkuSearch.trim();
    if (!trimmed) {
      setMatchedProducts([]);
      setSearchError("");
      return;
    }

    let cancelled = false;
    const runSearch = async () => {
      setSearchError("");
      try {
        const products = await onSearchSku(trimmed);
        if (cancelled) return;
        setMatchedProducts(products);
      } catch (error: unknown) {
        if (cancelled) return;
        setMatchedProducts([]);
        setSearchError(
          error instanceof Error ? error.message : "Failed to search products",
        );
      }
    };

    void runSearch();
    return () => {
      cancelled = true;
    };
  }, [debouncedSkuSearch, onSearchSku, open]);

  const filteredLines = useMemo(() => {
    const query = debouncedVariantSearch.trim().toLowerCase();
    if (!query) return lines;
    return lines.filter((line) => {
      const sku = line.sku.toLowerCase();
      const title = line.variantTitle.toLowerCase();
      const productName = line.productName.toLowerCase();
      const clientSku = line.clientSku.toLowerCase();
      return (
        sku.includes(query) ||
        title.includes(query) ||
        productName.includes(query) ||
        clientSku.includes(query)
      );
    });
  }, [debouncedVariantSearch, lines]);

  const selectedCount = useMemo(
    () => lines.filter((line) => line.selected).length,
    [lines],
  );

  const productOptions: FilterValue[] = useMemo(
    () =>
      matchedProducts
        .filter((product) => product.id != null)
        .map((product) => ({
          id: String(product.id),
          value:
            product.name?.trim() || product.sku?.trim() || String(product.id),
          subtitle: [
            product.sku?.trim() ? `SKU: ${product.sku.trim()}` : null,
            `ID: ${product.id}`,
          ]
            .filter(Boolean)
            .join(" · "),
        })),
    [matchedProducts],
  );

  const allVisibleSelected =
    filteredLines.length > 0 && filteredLines.every((line) => line.selected);
  const someVisibleSelected = filteredLines.some((line) => line.selected);

  const isLineAlreadyListed = (
    current: InwardLineForm[],
    productId: number,
    variantId?: number,
    sku?: string,
  ) =>
    current.some((line) => {
      if (line.productId !== productId) return false;
      if (variantId) return line.variantId === variantId;
      return (
        !line.variantId &&
        !!sku &&
        line.sku.trim().toLowerCase() === sku.trim().toLowerCase()
      );
    });

  const updateLine = (key: string, patch: Partial<InwardLineForm>) => {
    if (submitInProgress) return;
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
    if (patch.clientSku !== undefined) {
      setClientSkuErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
  };

  const handleAddProduct = async (product: ProductListApi) => {
    if (!product?.id || isFetchingVariants || submitInProgress) {
      return;
    }

    setIsFetchingVariants(true);
    setSearchError("");
    try {
      const variants = await onFetchVariants(product.id);
      const productId = product.id;
      const productName = product.name || "—";
      const current = linesRef.current;
      const nextLines: InwardLineForm[] = [];

      if (variants.length > 0) {
        variants.forEach((variant) => {
          const sku = variant.sku?.trim() || "";
          const variantId = variant.id > 0 ? variant.id : undefined;
          if (
            isLineAlreadyListed(
              current.concat(nextLines),
              productId,
              variantId,
              sku,
            )
          ) {
            return;
          }
          nextLines.push({
            key: newLineKey(productId, variantId, sku),
            variantId,
            productId,
            productName,
            variantTitle: variantTitle(variant, sku),
            sku,
            clientSku: sku,
            qty: "1",
            packSize: "",
            boxSize: "",
            tagId: "",
            tagLabel: "",
            selected: false,
          });
        });
      } else {
        const sku = product.sku?.trim() || String(productId);
        if (!isLineAlreadyListed(current, productId, undefined, sku)) {
          nextLines.push({
            key: newLineKey(productId, undefined, sku),
            productId,
            productName,
            variantTitle: productName,
            sku,
            clientSku: sku,
            qty: "1",
            packSize: "",
            boxSize: "",
            tagId: "",
            tagLabel: "",
            selected: false,
          });
        }
      }

      if (nextLines.length === 0) {
        setSearchError("All SKUs for this product are already listed");
        return;
      }

      const next = [...current, ...nextLines];
      linesRef.current = next;
      setLines(next);
    } catch (error: unknown) {
      setSearchError(
        error instanceof Error
          ? error.message
          : "Failed to load product variants",
      );
    } finally {
      setIsFetchingVariants(false);
    }
  };

  const handleSelectProduct = (selected: FilterValue[]) => {
    try {
      if (submitInProgress) return;
      const option = selected[0];
      if (!option) return;

      const product = matchedProducts.find(
        (item) => String(item.id) === String(option.id),
      );
      if (!product?.id) {
        setSearchError("Select a valid product");
        return;
      }

      void handleAddProduct(product);
    } catch (error: unknown) {
      setSearchError(
        error instanceof Error
          ? error.message
          : "Failed to add product from search",
      );
    }
  };

  const handleRemoveLine = (key: string) => {
    if (submitInProgress) return;
    setLines((current) => current.filter((line) => line.key !== key));
    setClientSkuErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const handleSelectAll = () => {
    if (submitInProgress) return;
    const visibleKeys = new Set(filteredLines.map((line) => line.key));
    const nextSelected = !allVisibleSelected;
    setLines((current) =>
      current.map((line) =>
        visibleKeys.has(line.key) ? { ...line, selected: nextSelected } : line,
      ),
    );
  };

  const validateLines = (): boolean => {
    const nextErrors: Record<string, string> = {};
    const seen = new Map<string, string>();
    const selectedLines = lines.filter((line) => line.selected);

    selectedLines.forEach((line) => {
      const clientSku = line.clientSku.trim();
      if (!clientSku) {
        nextErrors[line.key] = "Client SKU is required";
        return;
      }

      const existingKey = seen.get(clientSku.toLowerCase());
      if (existingKey) {
        nextErrors[line.key] = "Client SKU must be unique in this inward";
        nextErrors[existingKey] = "Client SKU must be unique in this inward";
        return;
      }
      seen.set(clientSku.toLowerCase(), line.key);

      const qtyRaw = String(line.qty ?? "").trim();
      const qty = Number(qtyRaw);
      if (
        qtyRaw === "" ||
        !Number.isFinite(qty) ||
        qty < 0 ||
        !Number.isInteger(qty)
      ) {
        nextErrors[line.key] = "Qty must be 0 or a positive whole number";
      }
    });

    setClientSkuErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitLockedRef.current || submitInProgress || isFetchingVariants) {
      return;
    }
    setSubmitError("");
    if (!validateLines()) return;

    const payload: InventoryInwardLineInput[] = lines
      .filter((line) => line.selected)
      .map((line) => {
        const packSizeRaw = line.packSize.trim();
        const packSize = packSizeRaw === "" ? undefined : Number(packSizeRaw);
        const sku = line.sku.trim();
        return {
          clientSku: line.clientSku.trim(),
          sku,
          productId: line.productId,
          catalogSku: sku,
          qty: Number(line.qty),
          packSize:
            packSize != null && Number.isFinite(packSize) && packSize >= 0
              ? packSize
              : undefined,
          boxSize: line.boxSize.trim() || undefined,
          tagId: line.tagId.trim() || undefined,
        };
      });

    isSubmitLockedRef.current = true;
    setIsSubmitLocked(true);

    try {
      await onSubmit(payload);
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : "Failed to inward variants. Please try again later.",
      );
    } finally {
      isSubmitLockedRef.current = false;
      setIsSubmitLocked(false);
    }
  };

  const handleClose = () => {
    if (submitInProgress) return;
    onClose();
  };

  const checkboxSx = {
    color: colors.purple,
    p: 0.5,
    "&.Mui-checked": { color: colors.purple },
    "&.MuiCheckbox-indeterminate": { color: colors.purple },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: colors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            overflow: "hidden",
            boxShadow: {
              xs: "none",
              sm: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
            background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.lightGreyColor} 100%)`,
            height: { xs: "100%", sm: "90vh" },
            maxHeight: { xs: "100%", sm: "90vh" },
            width: "100%",
            m: { xs: 0, sm: 2 },
            display: "flex",
            flexDirection: "column",
            position: "relative",
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", sm: "center" },
        },
      }}
    >
      <Backdrop
        open={submitInProgress}
        sx={{
          position: "absolute",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: colors.white,
          borderRadius: { xs: 0, sm: "16px" },
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${colors.blueGradientStart} 0%, ${colors.blueGradientEnd} 50%, ${colors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            borderBottom: `1px solid ${colors.greyBorder}`,
            flexShrink: 0,
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              Inward
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Search a product and select it to add its SKUs, then select the
              ones to inward.
            </CustomTypography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={submitInProgress}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, flexShrink: 0 }}>
            <CustomTypography variant="body2" fontWeight={600}>
              SEARCH PRODUCT
            </CustomTypography>
            <Grid2 container spacing={1.25} sx={{ mt: 0.75 }}>
              <Grid2 size={12}>
                <AppSearchDropdownMulti
                  isMultiSelect={false}
                  title="Search product by name or SKU"
                  list={productOptions}
                  value={[]}
                  handleSearch={(searchText) => {
                    setSkuSearch(searchText || "");
                  }}
                  handleSelect={handleSelectProduct}
                  disabled={isFetchingVariants || submitInProgress}
                  fontSize="12px"
                  minWidth="100%"
                />
              </Grid2>
            </Grid2>
            {(isFetchingVariants || searchError) && (
              <Box sx={{ mt: 1 }}>
                {isFetchingVariants ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <CustomTypography variant="body3" color="text.secondary">
                      Loading variants...
                    </CustomTypography>
                  </Box>
                ) : (
                  <CustomTypography
                    variant="body3"
                    sx={{ color: theme.palette.error.main }}
                  >
                    {searchError}
                  </CustomTypography>
                )}
              </Box>
            )}
          </Paper>

          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{ flex: 1, minHeight: 0 }}
          >
            {lines.length > 0 && (
              <Box
                display="flex"
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
                flexShrink={0}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ minWidth: { xs: "100%", sm: 280 }, flex: 1 }}
                >
                  <CustomTextField
                    value={variantSearch}
                    onChange={(e) => setVariantSearch(e.target.value)}
                    width="100%"
                    placeholder="Search SKU or title"
                    endAdornment
                    disabled={submitInProgress}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={0.25}>
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    disabled={filteredLines.length === 0 || submitInProgress}
                    onChange={handleSelectAll}
                    sx={checkboxSx}
                    inputProps={{ "aria-label": "Select all SKUs" }}
                  />
                  <CustomTypography
                    variant="body3"
                    fontWeight={600}
                    sx={{
                      cursor: filteredLines.length ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      if (filteredLines.length === 0) return;
                      handleSelectAll();
                    }}
                  >
                    Select all
                  </CustomTypography>
                </Box>
              </Box>
            )}

            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
            >
              {lines.length === 0 && (
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2 }}>
                  <CustomTypography variant="body2" color="text.secondary">
                    Search a product and select it to list its SKUs.
                  </CustomTypography>
                </Paper>
              )}

              {lines.length > 0 && filteredLines.length === 0 && (
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2 }}>
                  <CustomTypography variant="body2" color="text.secondary">
                    No SKU matches your search.
                  </CustomTypography>
                </Paper>
              )}

              {filteredLines.map((line) => {
                const lineError = clientSkuErrors[line.key];
                const fieldsDisabled = !line.selected || submitInProgress;
                return (
                  <Paper
                    key={line.key}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${colors.greyBorder}`,
                      backgroundColor: line.selected
                        ? colors.white
                        : colors.greyBodyBackground,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap={1}
                      alignItems="flex-start"
                      mb={1}
                    >
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={0.5}
                        minWidth={0}
                        flex={1}
                      >
                        <Checkbox
                          size="small"
                          checked={line.selected}
                          disabled={submitInProgress}
                          onChange={() =>
                            updateLine(line.key, { selected: !line.selected })
                          }
                          sx={{ ...checkboxSx, mt: -0.25, flexShrink: 0 }}
                          inputProps={{
                            "aria-label": `Select ${line.sku || line.variantTitle}`,
                          }}
                        />
                        <Box minWidth={0}>
                          <CustomTypography variant="body2" fontWeight={600}>
                            {line.variantTitle}
                          </CustomTypography>
                          <CustomTypography
                            variant="body3"
                            color="text.secondary"
                          >
                            {line.productName}
                          </CustomTypography>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveLine(line.key)}
                        disabled={submitInProgress}
                        aria-label="Remove line"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Grid2
                      container
                      spacing={1.25}
                      sx={{ opacity: fieldsDisabled ? 0.55 : 1 }}
                    >
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          SKU
                        </CustomTypography>
                        <Box sx={{ mt: 0.5 }}>
                          <CustomTextField
                            value={line.sku}
                            width="100%"
                            placeholder="SKU"
                            disabled
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          CLIENT SKU
                        </CustomTypography>
                        <Box sx={{ mt: 0.5 }}>
                          <CustomTextField
                            value={line.clientSku}
                            onChange={(e) =>
                              updateLine(line.key, {
                                clientSku: e.target.value,
                              })
                            }
                            width="100%"
                            placeholder="Enter client SKU"
                            disabled={fieldsDisabled}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4, md: 1.5 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          QTY
                        </CustomTypography>
                        <Box sx={{ mt: 0.5 }}>
                          <CustomTextField
                            type="number"
                            value={line.qty}
                            onChange={(e) =>
                              updateLine(line.key, { qty: e.target.value })
                            }
                            width="100%"
                            placeholder="Qty"
                            allowNegativeValue={false}
                            allowDecimalValue={false}
                            disabled={fieldsDisabled}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 6, sm: 4, md: 1.5 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          PACK SIZE
                        </CustomTypography>
                        <Box sx={{ mt: 0.5 }}>
                          <CustomTextField
                            type="number"
                            value={line.packSize}
                            onChange={(e) =>
                              updateLine(line.key, {
                                packSize: e.target.value,
                              })
                            }
                            width="100%"
                            placeholder="Optional"
                            allowNegativeValue={false}
                            allowDecimalValue={false}
                            disabled={fieldsDisabled}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4, md: 2 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          BOX SIZE
                        </CustomTypography>
                        <Box sx={{ mt: 0.5 }}>
                          <CustomTextField
                            value={line.boxSize}
                            onChange={(e) =>
                              updateLine(line.key, {
                                boxSize: e.target.value,
                              })
                            }
                            width="100%"
                            placeholder="Optional"
                            disabled={fieldsDisabled}
                          />
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                        <CustomTypography variant="body3" fontWeight={600}>
                          TAG
                        </CustomTypography>
                        <Box sx={{ mt: 0.5 }}>
                          <AppSearchDropdownMulti
                            isMultiSelect={false}
                            title="Select tag"
                            local
                            disabled={fieldsDisabled}
                            list={tagOptions}
                            value={
                              line.tagId
                                ? [
                                    {
                                      id: line.tagId,
                                      value: line.tagLabel || line.tagId,
                                    },
                                  ]
                                : []
                            }
                            handleSelect={(selected) => {
                              if (fieldsDisabled) return;
                              if (!selected.length) {
                                updateLine(line.key, {
                                  tagId: "",
                                  tagLabel: "",
                                });
                                return;
                              }
                              updateLine(line.key, {
                                tagId: String(selected[0].id),
                                tagLabel: selected[0].value,
                              });
                            }}
                            fontSize="12px"
                            minWidth="100%"
                          />
                        </Box>
                      </Grid2>
                    </Grid2>

                    {lineError && (
                      <CustomTypography
                        variant="body3"
                        sx={{
                          mt: 0.75,
                          color: theme.palette.error.main,
                          display: "block",
                        }}
                      >
                        {lineError}
                      </CustomTypography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Box>

          {submitError ? (
            <Alert
              severity="error"
              onClose={() => setSubmitError("")}
              sx={{ flexShrink: 0 }}
            >
              {submitError}
            </Alert>
          ) : null}

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1}
            flexWrap="wrap"
            sx={{ flexShrink: 0, pt: 0.5 }}
          >
            <AppButton
              label="Cancel"
              onClick={handleClose}
              variant="grayscale"
              disabled={submitInProgress}
              customStyles={{ width: { xs: "100%", sm: "auto" } }}
            />
            <AppButton
              label="Mark Inwarded"
              onClick={() => {
                if (submitInProgress) return;
                void handleSubmit();
              }}
              variant="primary"
              disabled={
                selectedCount === 0 || submitInProgress || isFetchingVariants
              }
              customStyles={{
                width: { xs: "100%", sm: "auto" },
                pointerEvents: submitInProgress ? "none" : "auto",
              }}
            />
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
